import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import './Style.css';

import SwiperCore, {
  Virtual,
  Keyboard,
  Mousewheel,
  Navigation,
  Scrollbar,
  Parallax,
  Zoom,
  Lazy,
  Controller,
  History,
  HashNavigation,
  Autoplay,
  EffectFade,
  EffectCube,
  EffectFlip,
  EffectCoverflow,
  Thumbs,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Button } from '../button/Button';
import { ContentItem } from './contentItem/ContentItem';

import 'swiper/scss';
import 'swiper/scss/navigation';

import { SlideContent } from './types';

interface SliderProps {
  media: SlideContent[];
  promo?: boolean;
  title?: string;
  id: string;
  onChangeSliderPos?: (data: { id: string; pos: number }) => void;
  dislikes?: string[];
  actionPlan?: boolean;
  target?: any;
  currentpos?: number;
}

const sliderBreakpoints = {
  1800: {
    slidesPerView: 5,
  },
  1440: {
    slidesPerView: 4,
  },
  1024: {
    slidesPerView: 3,
  },
  960: {
    slidesPerView: 2,
  },
  720: {
    slidesPerView: 2,
  },
  500: {
    slidesPerView: 1,
  },
};

export function Slider({
  media,
  promo = false,
  title,
  id,
  onChangeSliderPos,
  dislikes,
  actionPlan = false,
  target,
  currentpos,
}: SliderProps) {
  SwiperCore.use([
    Virtual,
    Keyboard,
    Mousewheel,
    Navigation,
    Scrollbar,
    Parallax,
    Zoom,
    Lazy,
    Controller,
    History,
    HashNavigation,
    Autoplay,
    EffectFade,
    EffectCube,
    EffectFlip,
    EffectCoverflow,
    Thumbs,
  ]);

  const { t } = useTranslation();
  const slidesList = media;
  let sliderLength = promo || actionPlan ? 4 : 5;

  const centeredSlides = false;
  let allowNext = true;
  let allowPrev = true;
  const spaceBetween = 5;

  // fix for short sliders
  if ((slidesList && slidesList.length < 5) || slidesList.length <= sliderLength) {
    allowNext = false;
    allowPrev = false;
  }

  if (window.innerWidth < 1300 && actionPlan) {
    sliderLength = 3;
  }
  if (window.innerWidth < 1050) {
    sliderLength = !actionPlan ? 4 : 3;
  }
  if (window.innerWidth < 800) {
    sliderLength = !actionPlan ? 3 : 2;
  }
  if (window.innerWidth < 600) {
    sliderLength = 2;
  }
  if (window.innerWidth < 500) {
    sliderLength = 1;
  }

  const swlength = sliderLength || 5;
  const swiperRef = useRef<any>(null);
  const allowSlideNext = allowNext;
  const allowSlidePrev = allowPrev;

  useEffect(() => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.update();
      if (document.getElementsByClassName('swiper-slide content_slider standard')[0]) {
        let width = (
          document.getElementsByClassName('swiper-slide content_slider standard')[0] as HTMLElement
        ).offsetWidth;
        if (window.innerWidth < 500) {
          width = 480;
        }
        const elements = document.getElementsByClassName('swiper content_slider standard');
        if (elements.length > 0) {
          let i;
          for (i = 0; i < elements.length; i++) {
            (elements[i] as HTMLElement).style.height = `${width}px`;
          }
        }
      }

      if (document.getElementsByClassName('swiper-slide content_slider tall')[0]) {
        let width = (
          document.getElementsByClassName('swiper-slide content_slider tall')[0] as HTMLElement
        ).offsetWidth;
        if (window.innerWidth < 500) {
          width = 480;
        }
        const elements = document.getElementsByClassName('swiper content_slider tall');
        if (elements.length > 0) {
          let i;
          for (i = 0; i < elements.length; i++) {
            (elements[i] as HTMLElement).style.height = `${Number(width) * 1.3}px`;
          }
        }
      }
    }
  }, [slidesList]);

  function sliderPosChange() {
    if (onChangeSliderPos && swiperRef.current && swiperRef.current.swiper) {
      const last = swiperRef.current.swiper.activeIndex - 1;
      onChangeSliderPos({ id, pos: last });
    }
  }

  const randoId = `sw${Math.floor(Math.random() * 90000)}${10000}`;
  const currentPosition = currentpos || 0;
  const labelId = `${id}-label`;
  const instructionsId = `${id}-instructions`;

  function getSlideClassName(slideContent: SlideContent, isPromo?: boolean): string {
    if (slideContent?.chid.startsWith('TA')) return 'content_slider standard green';
    if (isPromo) return 'content_slider tall';
    return 'content_slider standard';
  }

  return (
    <section
      className={`glowrm_slider grid-item-extra-wide ${slidesList[0].chid.startsWith('TA') ? 'green' : ''}`}
      id={id}
      role="group"
      aria-label={`${title || t('slider.promotion.title')} ${slidesList.length} ${t('slider.no.title.suffix')}.`}
      aria-roledescription={t('slider.description.carousel')}
    >
      {title && (
        <h2 className="fs-exclude slider_title" id={labelId}>
          {title.replace(/&amp;/gi, '&')}
        </h2>
      )}
      {slidesList.length > sliderLength && (
        <>
          <Button
            type="button"
            className="swiper-button-prev prev"
            id={`${randoId}-prev`}
            variant="ghost"
            ariaLabel={`${title || t('slider.promotion.title')} ${t('slider.group.title')} ${t('slider.previous.slide')}`}
          />
          <Button
            type="button"
            className="swiper-button-next next"
            id={`${randoId}-next`}
            variant="ghost"
            ariaLabel={`${title || t('slider.promotion.title')} ${t('slider.group.title')} ${t('slider.next.slide')}`}
          />
        </>
      )}

      <Swiper
        ref={swiperRef}
        spaceBetween={spaceBetween}
        slidesPerGroup={swlength}
        centeredSlides={centeredSlides}
        loopFillGroupWithBlank
        allowSlideNext={allowSlideNext}
        allowSlidePrev={allowSlidePrev}
        navigation={{
          nextEl: `#${randoId}-next`,
          prevEl: `#${randoId}-prev`,
        }}
        breakpoints={sliderBreakpoints}
        className={promo ? 'content_slider tall' : 'content_slider standard'}
        initialSlide={currentPosition}
        onSlideChange={() => sliderPosChange()}
        observer
        simulateTouch={false}
      >
        {slidesList.map((slideContent, index) => {
          if (
            slideContent !== null &&
            ((dislikes && !dislikes.includes(slideContent._id)) || !dislikes)
          ) {
            return (
              <SwiperSlide
                key={randoId + slideContent._id}
                virtualIndex={index}
                className={getSlideClassName(slideContent, promo)}
              >
                <ContentItem key={slideContent._id} slideContent={slideContent} promo={promo} />
              </SwiperSlide>
            );
          }
          return null;
        })}
      </Swiper>
    </section>
  );
}
